import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';

import actions from '../../../../redux/actions';
import cycleActions from '../../../../redux/actions/cycleActions';
import { RootState } from '../../../../redux/store';
import { TesterStatusInCycle } from '../../../../types/TesterStatusInCycle';
import { TestPlanStepStatus } from '../../../../types/TestPlanStepStatus';
import { PageContainer, PageSection, ToneChip } from '../../../../ui';
import { colors } from '../../../../theme/tokens';

const statusChip = {
  [TestPlanStepStatus.Passed]: <ToneChip label="Passed" tone="success" />,
  [TestPlanStepStatus.Failed]: <ToneChip label="Failed" tone="danger" />,
  [TestPlanStepStatus.Blocked]: <ToneChip label="Blocked" tone="neutral" />,
};

/* Tester → cycle → Test plan. Passed/Blocked are saved right away; Failed opens a new bug
   prefilled from the step (and linked to it). Read-only until the tester is in the cycle. */
const TesterTestPlan: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const cycleId = +id;
  const history = useHistory();
  const dispatch = useDispatch();
  const { cycles, resolution } = useSelector((state: RootState) => state);
  const isMobile = resolution.device === 'mobile' || resolution.device === 'mobile-tablet';

  const cycle: any = (cycles as any[]).find((el) => el.id === cycleId);

  if (!cycle || !cycle.testPlan) {
    return null;
  }

  const testPlanEditable = [TesterStatusInCycle.Approved, TesterStatusInCycle.AwaitingReview, TesterStatusInCycle.Finished].includes(cycle.testerStatus);
  const results: any[] = cycle.testPlanResults || [];

  const onFail = (step) => {
    dispatch(actions.updateNewBug({ testPlanStepId: step.id, stepsToReproduce: step.description, expectedResult: step.expectedResult }));
    history.push(`/account/tester/cycle/${cycleId}/newbug?testPlan=true`);
  };

  return (
    <PageContainer isMobile={isMobile}>
      <PageSection
        title="Test plan"
        padding={isMobile ? 1.5 : 2.5}
        actions={
          testPlanEditable && (
            <Button variant="contained" startIcon={<AddIcon />} component={RouterLink} to={`/account/tester/cycle/${cycleId}/newbug`}>
              {isMobile ? 'Bug' : 'Add bug without step'}
            </Button>
          )
        }
      >
        {cycle.testPlan.map((step, i) => {
          const status = results.find((result) => result.testPlanStepId === step.id)?.status || '';

          return (
            <Box
              key={step.id}
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 1 : 2,
                py: 2,
                borderTop: i ? '1px solid #f0f3f8' : 'none',
              }}
            >
              <Typography sx={{ width: 28, flex: 'none', fontWeight: 600, color: colors.textGrey }}>{i + 1}</Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{step.description}</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: colors.textGrey, mt: 1 }}>Expected result</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{step.expectedResult}</Typography>
              </Box>
              {testPlanEditable && (
                <Box sx={{ width: isMobile ? 'auto' : 250, flex: 'none', display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'flex-start' : 'flex-end', gap: 1 }}>
                  {statusChip[status]}
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button size="small" variant="outlined" color="success" disabled={status === TestPlanStepStatus.Passed}
                      onClick={() => dispatch(cycleActions.updateTestPlanStep(cycleId, step.id, TestPlanStepStatus.Passed))}>
                      Passed
                    </Button>
                    <Button size="small" variant="outlined" color="error" disabled={status === TestPlanStepStatus.Failed} onClick={() => onFail(step)}>
                      Failed
                    </Button>
                    <Tooltip title="Blocked is a test step that cannot run because the preconditions for its execution are not fulfilled">
                      <span>
                        <Button size="small" variant="outlined" color="inherit" disabled={status === TestPlanStepStatus.Blocked}
                          onClick={() => dispatch(cycleActions.updateTestPlanStep(cycleId, step.id, TestPlanStepStatus.Blocked))}>
                          Blocked
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}
      </PageSection>
    </PageContainer>
  );
};

export default TesterTestPlan;
